import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Loader2, AlertCircle,
  Filter, Users, ThumbsUp, ThumbsDown, Zap, RefreshCw, Activity
} from 'lucide-react';
import ProspectCard from './ProspectCard';
import ProspectEditModal from './ProspectEditModal';
import RunStatusPoller from './RunStatusPoller';
import { useToast } from '@/components/common/Toast';
import { Pagination } from '@/components/common/Pagination';
import { PageHeader } from '@/components/common/PageHeader';
import { useProspecting } from '../hooks/useProspecting';
import type { Prospect } from '../services/prospectingService';

const PAIN_FILTERS = ['ALL', 'VISIBILITY', 'EXECUTION', 'DATA', 'QUALITY', 'INTEGRATION'];
const STATUS_FILTERS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
const PROSPECTS_PER_PAGE = 12;

const PAIN_COLORS: Record<string, string> = {
  VISIBILITY:  'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  EXECUTION:   'bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  DATA:        'bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800',
  QUALITY:     'bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800',
  INTEGRATION: 'bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800',
};

export default function BatchReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const { addToast } = useToast();
  const {
    currentBatch, loading, approving,
    stagedApprovals, stagedRejections,
    fetchBatch, stageApproval, stageRejection, unstage, stageEdit, submitApprovals, resetReviewState,
  } = useProspecting();

  const [editingProspect, setEditingProspect]     = useState<Prospect | null>(null);
  const [painFilter, setPainFilter]               = useState('ALL');
  const [statusFilter, setStatusFilter]           = useState('ALL');
  const [refreshing, setRefreshing]               = useState(false);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);
  const [page, setPage]                           = useState(1);

  useEffect(() => {
    if (id) fetchBatch(id);
    return () => resetReviewState();
  }, [id, fetchBatch, resetReviewState]);

  const refresh = useCallback(async () => {
    if (!id) return;
    setRefreshing(true);
    try { await fetchBatch(id); } finally { setRefreshing(false); }
  }, [id, fetchBatch]);

  useEffect(() => { setPage(1); }, [painFilter, statusFilter]);

  const handleSubmit = useCallback(async () => {
    if (!id) return;
    setConfirmApproveAll(false);
    try {
      const result = await submitApprovals(id);
      if (result) {
        addToast({
          type: 'success',
          title: 'Accounts created',
          description: result.message,
        });
      }
    } catch (err: any) {
      addToast({ type: 'error', title: 'Approval failed', description: err.response?.data?.error || err.message });
    }
  }, [id, submitApprovals, addToast]);

  const handleApproveAllPending = useCallback(() => {
    const pending = (currentBatch?.prospects || []).filter(p =>
      p.reviewStatus === 'PENDING' && !stagedApprovals.has(p.id) && !stagedRejections.has(p.id)
    );
    if (pending.length === 0) return;
    pending.forEach(p => stageApproval(p.id));
    addToast({ type: 'info', title: `${pending.length} prospects staged`, description: 'Click "Submit approvals" to create accounts.' });
  }, [currentBatch, stagedApprovals, stagedRejections, stageApproval, addToast]);

  const prospects  = currentBatch?.prospects || [];
  const batchStatus = currentBatch?.status;

  const filtered = prospects.filter(p => {
    const eff = stagedApprovals.has(p.id) ? 'APPROVED'
      : stagedRejections.has(p.id) ? 'REJECTED'
      : p.reviewStatus;
    if (statusFilter !== 'ALL' && eff !== statusFilter) return false;
    if (painFilter  !== 'ALL' && p.primaryPainAxis !== painFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PROSPECTS_PER_PAGE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * PROSPECTS_PER_PAGE, safePage * PROSPECTS_PER_PAGE);

  const totalPending  = prospects.filter(p => p.reviewStatus === 'PENDING' && !stagedApprovals.has(p.id) && !stagedRejections.has(p.id)).length;
  const totalApproved = (currentBatch?.totalApproved || 0) + stagedApprovals.size;
  const totalRejected = (currentBatch?.totalRejected || 0) + stagedRejections.size;

  const painDist = prospects.reduce((acc, p) => {
    if (p.primaryPainAxis) acc[p.primaryPainAxis] = (acc[p.primaryPainAxis] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Loading skeleton
  if (loading && !currentBatch) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Not found
  if (!currentBatch) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p className="text-sm">Batch not found</p>
        <button onClick={() => navigate('/prospecting')} className="text-xs text-primary hover:underline">Back</button>
      </div>
    );
  }

  const criteria = currentBatch.criteria as any;

  return (
    <div className="space-y-5">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <PageHeader
        onBack={() => navigate('/prospecting')}
        title={`${criteria.targetIndustry} · ${criteria.region}`}
        description={
          new Date(currentBatch.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })
          + (currentBatch.triggeredBy?.name ? ` · ${currentBatch.triggeredBy.name}` : '')
          + (prospects.length > 0 ? ` · ${prospects.length} prospects` : '')
        }
        actions={
          <button onClick={refresh} disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* ── RUNNING state — show poller ─────────────────────────── */}
      {(batchStatus === 'RUNNING' || batchStatus === 'PENDING') && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4 text-primary animate-pulse" />
            Pipeline running — agents are researching prospects
          </div>
          <RunStatusPoller
            batchId={currentBatch.id}
            onComplete={(status) => {
              if (status === 'REVIEW') {
                addToast({ type: 'success', title: 'Prospects ready', description: 'Your batch is ready for review.' });
                refresh();
              } else if (status === 'FAILED') {
                addToast({ type: 'error', title: 'Pipeline failed', description: 'Check Agent Logs for details.' });
                refresh();
              }
            }}
          />
        </div>
      )}

      {/* ── FAILED state ─────────────────────────────────────────── */}
      {batchStatus === 'FAILED' && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Pipeline failed</p>
            <p className="text-xs text-red-600 dark:text-red-500 mt-0.5">
              {currentBatch.errorLog?.error || 'Unknown error. Check Agent Logs for details.'}
            </p>
            <button onClick={() => navigate('/prospecting/agent-logs')}
              className="text-xs text-primary hover:underline mt-2">View Agent Logs →</button>
          </div>
        </div>
      )}

      {/* ── REVIEW / APPROVED state ──────────────────────────────── */}
      {(batchStatus === 'REVIEW' || batchStatus === 'APPROVED') && (
        <>
          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total',    value: prospects.length,  icon: Users,      cls: 'text-foreground' },
              { label: 'Pending',  value: totalPending,      icon: Filter,     cls: 'text-amber-600 dark:text-amber-400' },
              { label: 'Approved', value: totalApproved,     icon: ThumbsUp,   cls: 'text-emerald-600 dark:text-emerald-400' },
              { label: 'Rejected', value: totalRejected,     icon: ThumbsDown, cls: 'text-red-500' },
            ].map(({ label, value, icon: Icon, cls }) => (
              <div key={label} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                <Icon className={`h-4 w-4 shrink-0 ${cls}`} />
                <div>
                  <p className={`text-lg font-bold leading-none ${cls}`}>{value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Pain axis breakdown */}
          {Object.keys(painDist).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(painDist).sort(([, a], [, b]) => b - a).map(([axis, count]) => (
                <button key={axis}
                  onClick={() => setPainFilter(painFilter === axis ? 'ALL' : axis)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    painFilter === axis ? 'ring-2 ring-primary ring-offset-1' : ''
                  } ${PAIN_COLORS[axis] || 'bg-muted text-muted-foreground border-border'}`}
                >
                  {axis} <span className="font-bold">{count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl border border-border bg-card/50">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status filter */}
              <div className="flex items-center gap-1">
                {STATUS_FILTERS.map(f => (
                  <button key={f} onClick={() => setStatusFilter(f)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}>{f}</button>
                ))}
              </div>
              {(stagedApprovals.size > 0 || stagedRejections.size > 0) && (
                <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-lg">
                  <span className="text-emerald-600 font-semibold">{stagedApprovals.size}</span> staged ·{' '}
                  <span className="text-red-500 font-semibold">{stagedRejections.size}</span> rejected
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {totalPending > 0 && stagedApprovals.size === 0 && (
                <button onClick={handleApproveAllPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors">
                  <CheckCircle className="h-3.5 w-3.5" />Approve all pending
                </button>
              )}

              {stagedApprovals.size > 0 && (
                !confirmApproveAll ? (
                  <button onClick={() => setConfirmApproveAll(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors">
                    <Zap className="h-4 w-4" />
                    Submit {stagedApprovals.size} approval{stagedApprovals.size !== 1 ? 's' : ''}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      Create {stagedApprovals.size} account{stagedApprovals.size !== 1 ? 's' : ''}?
                    </span>
                    <button onClick={handleSubmit} disabled={approving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50">
                      {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                      {approving ? 'Creating...' : 'Confirm'}
                    </button>
                    <button onClick={() => setConfirmApproveAll(false)}
                      className="px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors">
                      Cancel
                    </button>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Prospect grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Filter className="h-8 w-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No prospects match the current filters.</p>
              <button onClick={() => { setPainFilter('ALL'); setStatusFilter('ALL'); }}
                className="mt-2 text-xs text-primary hover:underline">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {paginated.map(prospect => (
                  <ProspectCard
                    key={prospect.id}
                    prospect={prospect}
                    isApproved={stagedApprovals.has(prospect.id)}
                    isRejected={stagedRejections.has(prospect.id)}
                    onApprove={() => stagedApprovals.has(prospect.id) ? unstage(prospect.id) : stageApproval(prospect.id)}
                    onReject={() => stagedRejections.has(prospect.id) ? unstage(prospect.id) : stageRejection(prospect.id)}
                    onEdit={() => setEditingProspect(prospect)}
                    onEnrich={() => refresh()}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  pageSize={PROSPECTS_PER_PAGE}
                  totalItems={filtered.length}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── Edit modal ───────────────────────────────────────────── */}
      {editingProspect && (
        <ProspectEditModal
          prospect={editingProspect}
          onSave={fields => {
            stageEdit(editingProspect.id, fields);
            setEditingProspect(null);
            addToast({ type: 'success', title: 'Edits saved', description: 'Staged for approval with your changes.' });
          }}
          onClose={() => setEditingProspect(null)}
        />
      )}
    </div>
  );
}
